import { Channel, Nullable } from 'chaika-frontend-types/frontend-cms';
import { Country, CountryCode, Currency, DO_NOT_NOTIFY, Languages, Locale } from 'chaika-enums';

import { OptionType } from '@components/Select';
import { translate } from '@chaika/i18n/helper';

import { DateHelper } from './DateHelper';
import { getEnumValues } from '@utils/getEnumValues';
import { getLanguagesTranslation } from '@utils/enumTranslation';

export const LocaleToLanguage: Record<Locale, Languages> = {
    [Locale.Ru]: Languages.Russian,
    [Locale.En]: Languages.English,
    [Locale.Ge]: Languages.Georgian,
    [Locale.Am]: Languages.Armenian,
    [Locale.Pt]: Languages.Portuguese,
};

export const CountryCodeToCountry: Record<CountryCode, Country> = {
    [CountryCode.Ru]: Country.Russia,
    [CountryCode.Ge]: Country.Georgia,
    [CountryCode.Am]: Country.Armenia,
    [CountryCode.Ae]: Country.UAE,
    [CountryCode.Pt]: Country.Portugal,
    [CountryCode.Gb]: Country.UnitedKingdom,
};

export function getLocaleArray(exlcludeLocale?: Locale): Locale[] {
    return CommonOptionType.getLocaleOptions()
        .filter(({ value }) => (!!exlcludeLocale ? value !== exlcludeLocale : true))
        .map(({ value }) => value);
}

export class CommonOptionType {
    public static getDefaultOption(): OptionType<null> {
        return {
            label: translate('all'),
            value: null,
        };
    }

    public static getCurrencyOptions(): OptionType<Currency>[] {
        return getEnumValues<Currency>(Currency, true).map(value => ({
            value,
            label: translate(value),
        }));
    }

    public static getLocaleOptions(): OptionType<Locale>[] {
        return getEnumValues<Locale>(Locale, true).map(value => ({
            value,
            label: LocaleToLanguage[value],
        }));
    }

    public static getCountryOptions(): OptionType<CountryCode>[] {
        return getEnumValues<CountryCode>(CountryCode, true).map(value => ({
            value,
            label: translate(CountryCodeToCountry[value]),
        }));
    }

    public static getTranslatedLocaleOptions(): OptionType<Locale>[] {
        return getEnumValues<Locale>(Locale, true).map(value => ({
            value,
            label: getLanguagesTranslation(value),
        }));
    }

    public static getDayOptions(): OptionType<number>[] {
        return [
            {
                value: 0,
                label: translate('mon'),
            },
            {
                value: 1,
                label: translate('tue'),
            },
            {
                value: 2,
                label: translate('wed'),
            },
            {
                value: 3,
                label: translate('thu'),
            },
            {
                value: 4,
                label: translate('fri'),
            },
            {
                value: 5,
                label: translate('sat'),
            },
            {
                value: 6,
                label: translate('sun'),
            },
        ];
    }

    public static getYearOptions(): OptionType<number>[] {
        const currentYear = DateHelper.getYear();
        const nextYear = currentYear + 1;
        const previousYear = currentYear - 1;

        return [
            {
                value: nextYear,
                label: `${nextYear}`,
            },
            {
                value: currentYear,
                label: `${currentYear}`,
            },
            {
                value: previousYear,
                label: `${previousYear}`,
            },
        ];
    }

    public static getMonthOptions(): OptionType<number>[] {
        return [
            {
                value: 0,
                label: translate('january'),
            },
            {
                value: 1,
                label: translate('february'),
            },
            {
                value: 2,
                label: translate('march'),
            },
            {
                value: 3,
                label: translate('april'),
            },
            {
                value: 4,
                label: translate('may'),
            },
            {
                value: 5,
                label: translate('june'),
            },
            {
                value: 6,
                label: translate('july'),
            },
            {
                value: 7,
                label: translate('august'),
            },
            {
                value: 8,
                label: translate('september'),
            },
            {
                value: 9,
                label: translate('october'),
            },
            {
                value: 10,
                label: translate('november'),
            },
            {
                value: 11,
                label: translate('december'),
            },
        ];
    }

    public static getPatientChannelOptions(): OptionType<Nullable<Channel | typeof DO_NOT_NOTIFY>>[] {
        return [
            {
                value: DO_NOT_NOTIFY,
                label: translate('doNotNotify'),
            },
            { value: Channel.Default, label: translate('default') },
        ];
    }
}
